import React, {createContext, useContext, useReducer} from "react";

const initialState = {
    bulkJobs: {},
    dataDownloads: {},
    metaDownloads: {},
    finishedBulkJobIds: [],
    subscriptionReady: false,
}

const BulkJobsStateContext = createContext(initialState);
const BulkJobsDispatchContext = createContext(initialState);

const reducer = (state, action) => {
    switch (action.type) {
        case 'add':
            const currentBulkJob = state.bulkJobs[action.bulkJob.guid];
            const terminalBulkJobStates = ['processed', 'errored', 'failed', 'canceled'];
            const currentBulkJobTime = Date.parse(currentBulkJob?.updated_at);
            const addedBulkJobTime = Date.parse(action.bulkJob.updated_at);
            const addedBulkJobIsNewer = Number.isFinite(currentBulkJobTime) &&
                Number.isFinite(addedBulkJobTime) &&
                addedBulkJobTime > currentBulkJobTime;
            const addedBulkJobIsOlder = Number.isFinite(currentBulkJobTime) &&
                Number.isFinite(addedBulkJobTime) &&
                addedBulkJobTime < currentBulkJobTime;
            const currentBulkJobTerminal = currentBulkJob && (
                currentBulkJob.done ||
                currentBulkJob.canceled ||
                terminalBulkJobStates.includes(currentBulkJob.aasm_state)
            );
            const addedBulkJobTerminal = (
                action.bulkJob.done ||
                action.bulkJob.canceled ||
                terminalBulkJobStates.includes(action.bulkJob.aasm_state)
            );
            if(addedBulkJobIsOlder) return state;

            // A fast job can finish over ActionCable before the POST response
            // is added. Do not overwrite that terminal update with a pending
            // response snapshot, while still accepting a newer server state.
            if(currentBulkJobTerminal && !addedBulkJobTerminal && !addedBulkJobIsNewer) return state;

            state.bulkJobs[action.bulkJob.guid] = {...currentBulkJob, ...action.bulkJob}
            return {...state, bulkJobs: {...state.bulkJobs}};

        case 'setSubscriptionReady':
            return {...state, subscriptionReady: action.ready};

        // Received from ActionCable.jsx subscription:
        case 'progress':
            const bj = state.bulkJobs[action.guid] || {guid: action.guid}
            const terminalStates = ['processed', 'errored', 'failed', 'canceled'];
            const alreadyTerminal = bj.done || bj.canceled || terminalStates.includes(bj.aasm_state);
            const incomingTerminal = action.done || action.canceled || terminalStates.includes(action.aasm_state);
            const currentStateTime = Date.parse(bj.updated_at);
            const incomingStateTime = Date.parse(action.updated_at);
            const incomingStateIsNewer = Number.isFinite(currentStateTime) &&
                Number.isFinite(incomingStateTime) &&
                incomingStateTime > currentStateTime;
            const incomingStateIsOlder = Number.isFinite(currentStateTime) &&
                Number.isFinite(incomingStateTime) &&
                incomingStateTime < currentStateTime;
            // Polls and ActionCable can cross in either direction. Server
            // updated_at is the ordering authority whenever both updates have
            // one, regardless of whether either state is terminal.
            if(incomingStateIsOlder) return state;

            // A polling response can have been requested before a terminal
            // ActionCable update. Never let that stale response regress the
            // job, while allowing a newer server transition to resume a
            // Sidekiq retry from errored to processing.
            if(alreadyTerminal && !incomingTerminal && !incomingStateIsNewer) return state;

            bj.progress = action.progress;
            bj.total_assets = action.total_assets
            bj.added = action.added;
            bj.removed = action.removed;
            bj.done = action.done;
            [
                'removal',
                'removed_count',
                'skipped_count',
                'failed_count',
                'operation_counts',
                'aasm_state',
                'updated_at',
                'asset_group_move',
                'selected_asset_group_count',
                'processed_asset_groups',
                'affected_asset_groups_count',
                'reindexing_asset_groups_count',
                'indexing_pending',
                'indexing_status'
            ].forEach(key => {
                if(Object.prototype.hasOwnProperty.call(action, key)) {
                    bj[key] = action[key];
                }
            });
            if(Object.prototype.hasOwnProperty.call(action, 'error')) {
                bj.error = action.error;
            }
            bj.warnings = action.warnings;
            bj.canceled = action.canceled;
            bj.lastUpdate = Date.now();
            state.bulkJobs[action.guid] = bj

            if(bj.progress === 100 && action.done) {
                if(!state.finishedBulkJobIds.includes(action.guid)) {
                    state.finishedBulkJobIds.push(action.guid)
                }
            }

            return {...state, bulkJobs: {...state.bulkJobs}};

        case 'dataDownload':
            state.dataDownloads[action.data.guid] = action.data
            return {...state, dataDownloads: {...state.dataDownloads}}

        case 'metaDownloadProgress':
            state.metaDownloads[action.data.guid] = action.data
            return {...state, metaDownloads: {...state.metaDownloads}}

        default:
            return state
    }
}

const BulkJobsProvider = ({children})=> {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <BulkJobsStateContext.Provider value={state}>
            <BulkJobsDispatchContext.Provider value={dispatch}>
                {children}
            </BulkJobsDispatchContext.Provider>
        </BulkJobsStateContext.Provider>
    )
}

function useBulkJobsState() {
    const context = useContext(BulkJobsStateContext)
    if (context === undefined) {
        throw new Error('useBulkJobsState must be used within a Provider')
    }
    return context
}

function useBulkJobsDispatch() {
    const context = useContext(BulkJobsDispatchContext)
    if (context === undefined) {
        throw new Error('useBulkJobsDispatch must be used within a Provider')
    }
    return context
}

export {
    initialState,
    reducer,
    BulkJobsProvider,
    useBulkJobsState,
    useBulkJobsDispatch
};
